import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, Formik, Form, Field, FieldArray } from 'formik';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';

import { Alert, DialogTitleWithClose } from 'ui';
import { sharePortfolioSchema } from 'shared/validations';

const useStyles = makeStyles(() => ({
  dialogContent: {
    minWidth: '400px',
  },
}));

const handleSubmit = (values, { setSubmitting }) => {
  setTimeout(() => {
    setSubmitting(false);
  }, 400);
};

const SharePortfolioModal = ({ onClose, open, users }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const initialValues = {
    users: [],
    message: '',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}>
      <DialogTitleWithClose onClose={onClose}>
        {t('Share Portfolio with user')}
      </DialogTitleWithClose>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={sharePortfolioSchema}
      >
        {({ errors, isSubmitting, touched, values, setFieldValue }) => {
          const handleSelectAllClick = () =>
            setFieldValue(
              'users',
              users.map(user => user.id),
            );

          return (
            <Form noValidate>
              <DialogContent className={classes.dialogContent}>
                <ErrorMessage name="users">
                  {message => (
                    <Box mb={1}>
                      <Alert message={message} variant="error" />
                    </Box>
                  )}
                </ErrorMessage>
                <Button onClick={handleSelectAllClick}>{t('Select all')}</Button>
                <FormGroup>
                  <FieldArray name="users">
                    {arrayHelpers => {
                      const handleChange = (event, userId) => {
                        event.persist();
                        const { target } = event;

                        if (target.checked) {
                          arrayHelpers.push(userId);
                        } else {
                          arrayHelpers.remove(values.users.indexOf(userId));
                        }
                      };

                      return users.map(user => (
                        <FormControlLabel
                          key={user.id}
                          label={user.fullName}
                          control={
                            <Checkbox
                              checked={values.users.includes(user.id)}
                              onChange={event => handleChange(event, user.id)}
                            />
                          }
                        />
                      ));
                    }}
                  </FieldArray>
                </FormGroup>
                <Field name="message">
                  {({ field }) => (
                    <TextField
                      {...field}
                      type="text"
                      label={t('Message to user(s)')}
                      error={errors.message && touched.message}
                      fullWidth
                      multiline
                      rows={7}
                    />
                  )}
                </Field>
              </DialogContent>
              <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                  {t('Cancel')}
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  {t('Save')}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

SharePortfolioModal.defaultProps = {
  open: false,
  onClose: () => null,
};

SharePortfolioModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      fullName: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default SharePortfolioModal;
